import { CubePresenter, FlipIntent, Logger, NOOP_VOID, ScopeSlot } from 'src/3rds/cube'
import { MainPresenter } from 'src/main/Main.presenter'
import { AppStorage } from 'src/utils'
import { CadastroLoteKeys } from './CadastroLote.keys'
import { CadastroLoteScope, Parceiro } from './CadastroLote.scopes'
import { CadastroLoteService, conferirCnpjs } from './CadastroLote.service'

const LOG = Logger.get('CadastroLotePresenter')
const service = CadastroLoteService.singleton()
const appStorage = AppStorage.singleton()

// Enquanto o lote roda, a tela repergunta o andamento neste intervalo.
const POLLING_MS = 2000

// A base tem milhares de parceiros, então o filtro é feito no servidor. Espera o operador
// parar de digitar antes de consultar.
const DEBOUNCE_BUSCA_MS = 300

export const ROLES_AUTORIZADOS = new Set(['admin', 'manager'])

export function podeCadastrarEmLote() {
    return ROLES_AUTORIZADOS.has(`${appStorage.userRole ?? ''}`)
}

export class CadastroLotePresenter extends CubePresenter<MainPresenter, CadastroLoteScope> {
    private parentSlot: ScopeSlot = NOOP_VOID
    private pollingTimer?: ReturnType<typeof setTimeout>
    private buscaParceiroTimer?: ReturnType<typeof setTimeout>

    constructor(app: MainPresenter) {
        super(app, new CadastroLoteScope())
    }

    public override release() {
        this.#pararPolling()
        this.#cancelarBuscaAgendada()
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new CadastroLoteKeys(this.app, intent)

        if (initialization) {
            if (!podeCadastrarEmLote()) {
                this.app.alert('warning', 'Acesso restrito', 'O cadastro em lote é restrito a administradores.')
                keys.allow = false
                return keys.allow
            }

            this.parentSlot = keys.parentSlot
            await this.#initialize()
        }

        if (keys.loteId && keys.loteId !== this.scope.lote?.id) {
            await this.#abrirLote(keys.loteId)
        }

        this.parentSlot(this.scope)

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new CadastroLoteKeys(this.app, intent)
        keys.loteId = this.scope.lote?.id ?? ''
    }

    async #initialize() {
        LOG.debug('initialize')

        this.scope.onSelecionarParceiro = this.action(this.#handleSelecionarParceiro, this)
        this.scope.onBuscarParceiro = this.action(this.#handleBuscarParceiro, this)
        this.scope.onTextoCnpjsChange = this.action(this.#handleTextoCnpjsChange, this)
        this.scope.onConferir = this.action(this.#handleConferir, this)
        this.scope.onFecharConfirmacao = this.action(this.#handleFecharConfirmacao, this)
        this.scope.onConfirmarDisparo = this.action(this.#handleConfirmarDisparo, this)
        this.scope.onCancelarLote = this.action(this.#handleCancelarLote, this)
        this.scope.onBaixarCsv = this.action(this.#handleBaixarCsv, this)
        this.scope.onAbrirLote = this.action(this.#handleAbrirLote, this)
        this.scope.onNovoLote = this.action(this.#handleNovoLote, this)

        await Promise.all([this.#carregarParceiros(), this.#carregarHistorico()])
    }

    // :: Carga

    async #carregarParceiros(busca?: string) {
        this.scope.carregandoParceiros = true
        try {
            const parceiros = await service.listarParceiros(busca)

            // O selecionado precisa continuar entre as opções, senão o Autocomplete perde o valor
            // quando a busca muda.
            const selecionado = this.scope.parceiroSelecionado
            if (selecionado && !parceiros.some((item) => item.ownerId === selecionado.ownerId)) {
                parceiros.unshift(selecionado)
            }

            this.scope.parceiros = parceiros
        } catch (error) {
            this.unexpected('Carregando parceiros', error)
        } finally {
            this.scope.carregandoParceiros = false
        }
    }

    async #carregarHistorico() {
        this.scope.carregandoHistorico = true
        try {
            this.scope.historico = await service.listarHistorico()
        } catch (error) {
            this.unexpected('Carregando histórico de lotes', error)
        } finally {
            this.scope.carregandoHistorico = false
        }
    }

    // :: Formulário

    async #handleBuscarParceiro(texto: string) {
        this.scope.buscaParceiro = texto
        this.#cancelarBuscaAgendada()
        this.buscaParceiroTimer = setTimeout(() => {
            this.buscaParceiroTimer = undefined
            this.#carregarParceiros(texto.trim() || undefined).catch((error) => LOG.error('Buscando parceiros', error))
        }, DEBOUNCE_BUSCA_MS)
    }

    #cancelarBuscaAgendada() {
        if (this.buscaParceiroTimer) {
            clearTimeout(this.buscaParceiroTimer)
            this.buscaParceiroTimer = undefined
        }
    }

    async #handleSelecionarParceiro(parceiro?: Parceiro) {
        this.scope.parceiroSelecionado = parceiro
        this.scope.erroFormulario = undefined
    }

    async #handleTextoCnpjsChange(texto: string) {
        this.scope.textoCnpjs = texto
        this.scope.previa = undefined
        this.scope.erroFormulario = undefined
    }

    async #handleConferir() {
        if (!this.scope.parceiroSelecionado) {
            this.scope.erroFormulario = 'Escolha o parceiro que será o dono das negociações.'
            return
        }

        const previa = conferirCnpjs(this.scope.textoCnpjs)
        if (previa.validos.length === 0) {
            this.scope.previa = previa
            this.scope.erroFormulario = 'Nenhum CNPJ válido foi informado.'
            return
        }

        this.scope.erroFormulario = undefined
        this.scope.previa = previa
        this.scope.confirmacaoAberta = true
    }

    async #handleFecharConfirmacao() {
        this.scope.confirmacaoAberta = false
    }

    async #handleConfirmarDisparo() {
        const parceiro = this.scope.parceiroSelecionado
        const previa = this.scope.previa
        if (!parceiro || !previa || previa.validos.length === 0) {
            return
        }

        this.scope.enviando = true
        try {
            const lote = await service.criarLote(parceiro.ownerId, previa.validos)
            this.scope.confirmacaoAberta = false
            this.scope.textoCnpjs = ''
            this.scope.previa = undefined
            await this.#abrirLote(lote.id)
            await this.#carregarHistorico()
            this.app.notify('success', `Lote ${lote.id} iniciado com ${lote.total} CNPJs`)
        } catch (error) {
            this.unexpected('Disparando o lote', error)
        } finally {
            this.scope.enviando = false
        }
    }

    async #handleNovoLote() {
        this.#pararPolling()
        this.scope.lote = undefined
        this.scope.previa = undefined
        this.scope.textoCnpjs = ''
        this.scope.erroFormulario = undefined
        this.updateHistory()
    }

    // :: Acompanhamento

    async #handleAbrirLote(loteId: string) {
        await this.#abrirLote(loteId)
    }

    async #abrirLote(loteId: string) {
        this.scope.carregandoLote = true
        try {
            await this.#atualizarLote(loteId)
            this.updateHistory()
        } catch (error) {
            this.unexpected('Abrindo o lote', error)
        } finally {
            this.scope.carregandoLote = false
        }
    }

    async #atualizarLote(loteId: string) {
        const lote = await service.obterLote(loteId)
        this.scope.lote = lote

        // Só continua perguntando enquanto o lote está em andamento.
        if (lote.status === 'pendente' || lote.status === 'processando') {
            this.#agendarPolling(loteId)
        } else {
            this.#pararPolling()
            await this.#carregarHistorico()
        }
    }

    #agendarPolling(loteId: string) {
        this.#pararPolling()
        this.pollingTimer = setTimeout(() => {
            this.pollingTimer = undefined
            this.#atualizarLote(loteId).catch((error) => LOG.error('Atualizando o lote', error))
        }, POLLING_MS)
    }

    #pararPolling() {
        if (this.pollingTimer) {
            clearTimeout(this.pollingTimer)
            this.pollingTimer = undefined
        }
    }

    async #handleCancelarLote() {
        const lote = this.scope.lote
        if (!lote) return

        try {
            await service.cancelarLote(lote.id)
            this.app.notify('info', 'Cancelamento solicitado; o CNPJ em andamento ainda será concluído')
            await this.#atualizarLote(lote.id)
        } catch (error) {
            this.unexpected('Cancelando o lote', error)
        }
    }

    async #handleBaixarCsv() {
        const lote = this.scope.lote
        if (!lote) return

        this.scope.baixandoCsv = true
        try {
            const { nome, blob } = await service.baixarCsv(lote.id)
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = nome
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        } catch (error) {
            this.unexpected('Baixando o CSV', error)
        } finally {
            this.scope.baixandoCsv = false
        }
    }
}

export default CadastroLotePresenter
